/* <i80-timeline>: an i80 component of our own (not in Helios), built only from i80 tokens. */
.i80-timeline {
  --i80-timeline-label-width: 176px;
  --i80-timeline-hour: calc(100% / 24);
  overflow-x: auto;
  color: var(--i80-color-foreground-primary);
  font-family: var(--i80-typography-font-stack-text);
  background: var(--i80-color-surface-primary);
  border: 1px solid var(--i80-color-border-primary);
  border-radius: var(--i80-border-radius-medium);
}

.i80-timeline__sr {
  display: none;
}

.i80-timeline__row,
.i80-timeline__lane {
  display: grid;
  grid-template-columns: var(--i80-timeline-label-width) minmax(560px, 1fr);
  min-width: calc(var(--i80-timeline-label-width) + 560px);
}

.i80-timeline__corner,
.i80-timeline__lane-head {
  position: sticky;
  left: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-width: 0;
  padding: 4px 12px;
  background: var(--i80-color-surface-primary);
  border-right: 1px solid var(--i80-color-border-primary);
}

.i80-timeline__corner {
  color: var(--i80-color-foreground-strong);
  font-weight: var(--i80-typography-font-weight-semibold);
  font-size: var(--i80-typography-body-200-font-size);
}

.i80-timeline__lane-label {
  overflow: hidden;
  color: var(--i80-color-foreground-strong);
  font-weight: var(--i80-typography-font-weight-medium);
  font-size: var(--i80-typography-body-200-font-size);
  line-height: 20px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.i80-timeline__lane-detail {
  overflow: hidden;
  color: var(--i80-color-foreground-faint);
  font-size: var(--i80-typography-body-100-font-size);
  line-height: 16px;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* Hour axis */
.i80-timeline__row--axis {
  border-bottom: 1px solid var(--i80-color-border-primary);
}

.i80-timeline__axis {
  position: relative;
  height: 28px;
}

.i80-timeline__tick {
  position: absolute;
  bottom: 4px;
  padding-left: 4px;
  color: var(--i80-color-foreground-faint);
  font-size: var(--i80-typography-body-100-font-size);
  font-variant-numeric: tabular-nums;
  border-left: 1px solid var(--i80-color-border-primary);
}

/* Coverage strip: the fill is what is planned, the line on top what is needed. */
.i80-timeline__row--coverage {
  border-bottom: 1px solid var(--i80-color-border-primary);
}

.i80-timeline__coverage {
  position: relative;
  height: 56px;
  background-image: linear-gradient(to right, var(--i80-color-border-faint) 1px, transparent 1px);
  background-size: var(--i80-timeline-hour) 100%;
}

.i80-timeline__run {
  position: absolute;
  top: 6px;
  bottom: 0;
  padding: 0;
  font: inherit;
  background: transparent;
  border: 0;
}

.i80-timeline__run::after {
  position: absolute;
  right: 0;
  bottom: calc(var(--i80-timeline-need) * 100%);
  left: 0;
  border-top: 2px solid var(--i80-color-foreground-strong);
  content: "";
}

.i80-timeline__fill {
  position: absolute;
  right: 1px;
  bottom: 0;
  left: 0;
  height: calc(var(--i80-timeline-have) * 100%);
  background: var(--i80-color-palette-green-100);
  border-top: 2px solid var(--i80-color-palette-green-200);
}

.i80-timeline__run--over .i80-timeline__fill {
  background: var(--i80-color-palette-blue-100);
  border-color: var(--i80-color-palette-blue-200);
}

.i80-timeline__run--short {
  cursor: pointer;
  background: repeating-linear-gradient(135deg, var(--i80-color-surface-critical) 0 5px, var(--i80-color-palette-red-100) 5px 10px);
}

.i80-timeline__run--short .i80-timeline__fill {
  background: var(--i80-color-palette-red-100);
  border-color: var(--i80-color-palette-red-200);
}

.i80-timeline__run--short::after {
  border-top-color: var(--i80-color-foreground-critical);
}

.i80-timeline__run--short:hover,
.i80-timeline__run--short:focus-visible {
  outline: 2px solid var(--i80-color-border-critical);
  outline-offset: -2px;
}

/* Lanes */
.i80-timeline__lane {
  border-bottom: 1px solid var(--i80-color-border-faint);
}

.i80-timeline__lane:last-child {
  border-bottom: 0;
}

.i80-timeline__lane--readonly .i80-timeline__lane-head,
.i80-timeline__lane--uncounted .i80-timeline__lane-head {
  background: var(--i80-color-surface-faint);
}

.i80-timeline__lane--uncounted {
  border-bottom: 1px solid var(--i80-color-border-primary);
}

.i80-timeline__lane--marked .i80-timeline__lane-head,
.i80-timeline__lane--marked .i80-timeline__track {
  background-color: var(--i80-color-surface-action);
}

.i80-timeline__track {
  position: relative;
  height: 40px;
  background-image: linear-gradient(to right, var(--i80-color-border-faint) 1px, transparent 1px);
  background-size: var(--i80-timeline-hour) 100%;
}

.i80-timeline__lane--uncounted .i80-timeline__track {
  background-color: var(--i80-color-surface-faint);
}

.i80-timeline__track--target {
  background-color: var(--i80-color-surface-action);
  box-shadow: inset 0 0 0 2px var(--i80-color-border-action);
}

/* Bars */
.i80-timeline__bar {
  position: absolute;
  top: 5px;
  bottom: 5px;
  display: flex;
  gap: 6px;
  align-items: center;
  min-width: 8px;
  overflow: hidden;
  padding: 0 8px;
  color: var(--i80-timeline-bar-fg);
  font: inherit;
  font-size: var(--i80-typography-body-100-font-size);
  line-height: 16px;
  white-space: nowrap;
  text-align: left;
  background: var(--i80-timeline-bar-bg);
  border: 1px solid var(--i80-timeline-bar-border);
  border-left-width: 3px;
  border-radius: var(--i80-border-radius-small);
  cursor: pointer;
  touch-action: none;
  user-select: none;
}

.i80-timeline__bar--draggable {
  cursor: grab;
}

.i80-timeline__bar--dragging {
  z-index: 3;
  cursor: grabbing;
  box-shadow: var(--i80-elevation-high-box-shadow);
  opacity: 0.92;
}

.i80-timeline__bar:focus-visible {
  outline: 3px solid var(--i80-color-focus-action-external);
  outline-offset: 1px;
}

.i80-timeline__bar--selected {
  outline: 2px solid var(--i80-color-foreground-strong);
  outline-offset: 1px;
}

.i80-timeline__bar-text {
  font-weight: var(--i80-typography-font-weight-semibold);
}

.i80-timeline__bar-time {
  opacity: 0.8;
  font-variant-numeric: tabular-nums;
}

.i80-timeline__bar-icon {
  flex: none;
  margin-left: auto;
}

.i80-timeline__bar--neutral { --i80-timeline-bar-bg: var(--i80-color-palette-neutral-100); --i80-timeline-bar-fg: var(--i80-color-palette-neutral-600); --i80-timeline-bar-border: var(--i80-color-palette-neutral-400); }
.i80-timeline__bar--blue { --i80-timeline-bar-bg: var(--i80-color-palette-blue-100); --i80-timeline-bar-fg: var(--i80-color-palette-blue-500); --i80-timeline-bar-border: var(--i80-color-palette-blue-200); }
.i80-timeline__bar--purple { --i80-timeline-bar-bg: var(--i80-color-palette-purple-100); --i80-timeline-bar-fg: var(--i80-color-palette-purple-500); --i80-timeline-bar-border: var(--i80-color-palette-purple-200); }
.i80-timeline__bar--green { --i80-timeline-bar-bg: var(--i80-color-palette-green-100); --i80-timeline-bar-fg: var(--i80-color-palette-green-500); --i80-timeline-bar-border: var(--i80-color-palette-green-200); }
.i80-timeline__bar--amber { --i80-timeline-bar-bg: var(--i80-color-palette-amber-100); --i80-timeline-bar-fg: var(--i80-color-palette-amber-500); --i80-timeline-bar-border: var(--i80-color-palette-amber-200); }
.i80-timeline__bar--red { --i80-timeline-bar-bg: var(--i80-color-palette-red-100); --i80-timeline-bar-fg: var(--i80-color-palette-red-500); --i80-timeline-bar-border: var(--i80-color-palette-red-200); }

.i80-timeline__bar--striped {
  background: repeating-linear-gradient(135deg, var(--i80-timeline-bar-bg) 0 6px, var(--i80-color-surface-primary) 6px 12px);
}

.i80-timeline__bar--outline {
  background: var(--i80-color-surface-primary);
  border-style: dashed;
}

/* Status: a ring and an icon, so it doesn't rest on colour alone. */
.i80-timeline__bar--soft {
  box-shadow: 0 0 0 2px var(--i80-color-border-warning);
}

.i80-timeline__bar--soft .i80-timeline__bar-icon {
  color: var(--i80-color-foreground-warning);
}

.i80-timeline__bar--hard {
  box-shadow: 0 0 0 2px var(--i80-color-border-critical);
}

.i80-timeline__bar--hard .i80-timeline__bar-icon {
  color: var(--i80-color-foreground-critical);
}

.i80-timeline__handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 8px;
  cursor: ew-resize;
}

.i80-timeline__handle--start { left: -3px; }
.i80-timeline__handle--end { right: 0; }

.i80-timeline__bar:hover .i80-timeline__handle--end {
  background: linear-gradient(to right, transparent 3px, var(--i80-timeline-bar-border) 3px 5px, transparent 5px);
}

@media (max-width: 600px) {
  .i80-timeline {
    --i80-timeline-label-width: 112px;
  }
}
